<template>
  <div id="ldmt-lom-status-list">
    <overlay-loading-progress :LoadingFlag="OverlayLoadingFlag" />

    <!-- Professional Main Card Container -->
    <v-card class="main-container" elevation="2">
      <!-- Professional Header Section -->
      <v-card-title class="professional-header pa-6">
        <div class="header-content">
          <div class="title-section">
            <div class="main-title-wrapper">
              <v-icon size="32" color="#3A67B1" class="mr-3">mdi-account-supervisor</v-icon>
              <div class="title-text">
                <h1 class="page-main-title">{{ PageTitle }}</h1>
                <div class="year-badge">
                  <v-chip color="#3A67B1" text-color="white" small>
                    <v-icon left size="14">mdi-calendar</v-icon>
                    {{ YearName }}
                  </v-chip>
                </div>
              </div>
            </div>
          </div>

          <div class="action-section">
            <v-btn
              color="#56BDA3"
              elevation="2"
              rounded
              class="refresh-btn white--text"
              @click.prevent="refreshPageData"
            >
              <v-icon left size="18">mdi-refresh</v-icon>
              Refresh Data
            </v-btn>
          </div>
        </div>

        <!-- Professional Subtitle Section -->
        <div class="subtitle-section mt-4" v-if="PageDescription">
          <div class="subtitle-wrapper">
            <v-icon size="20" color="#414042" class="mr-2">mdi-information-outline</v-icon>
            <p class="page-description">{{ PageDescription }}</p>
          </div>
        </div>
      </v-card-title>

      <!-- Professional Dialog for Eligibility Details -->
      <v-dialog
        v-model="PreviewPlanFlag"
        max-width="600"
        persistent
        transition="dialog-transition"
        overlay-opacity="0.7"
      >
        <eligibility-details-popup
          :Plan="PreviewPlanDetails"
          @hideDialog="hidePreviewPlanPopup($event)"
          v-if="PreviewPlanFlag"
        ></eligibility-details-popup>
      </v-dialog>

      <!-- Professional Content Container -->
      <v-card-text class="main-content pa-6">
        <!-- Professional Loading Section -->
        <div class="loading-section" v-if="LoadingFlag">
          <div class="loading-wrapper">
            <v-progress-circular
              indeterminate
              color="#3A67B1"
              size="48"
              width="4"
            ></v-progress-circular>
            <p class="loading-text mt-4">Loading nomination data...</p>
          </div>
        </div>

        <!-- Dashboard Cards Component -->
        <nomination-dashboard-cards
          v-if="!LoadingFlag"
          :dashboardInfo="memberData"
        />

        <!-- Professional Video Tutorial Section -->
        <div class="video-tutorial-section" v-if="!LoadingFlag">
          <v-row justify="center">
            <v-col cols="12" lg="10">
              <v-card class="tutorial-card" elevation="3">
                <v-card-title class="tutorial-header">
                  <div class="tutorial-title-section">
                    <v-icon color="#FF6B6B" size="28" class="mr-3">mdi-play-circle</v-icon>
                    <div class="title-content">
                      <h3 class="tutorial-title">How to Submit Your Nomination</h3>
                      <p class="tutorial-subtitle">Watch this step-by-step tutorial to learn the nomination process</p>
                    </div>
                  </div>
                  <v-btn
                    icon
                    small
                    @click="toggleVideoSection"
                    class="expand-btn"
                  >
                    <v-icon>{{ showVideoSection ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  </v-btn>
                </v-card-title>

                <v-expand-transition>
                  <v-card-text class="tutorial-content pa-6" v-show="showVideoSection">
                    <v-row>
                      <v-col cols="12" md="8">
                        <!-- YouTube Video Embed -->
                        <div class="video-container">
                          <div class="video-wrapper">
                            <iframe
                              :src="youtubeEmbedUrl"
                              frameborder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowfullscreen
                              class="video-iframe"
                            ></iframe>
                          </div>
                        </div>
                      </v-col>
                      <v-col cols="12" md="4">
                        <!-- Video Information -->
                        <div class="video-info">
                          <div class="info-section">
                            <h4 class="info-title">What You'll Learn</h4>
                            <ul class="learning-points">
                              <li>How to access the nomination form</li>
                              <li>Required documents and information</li>
                              <li>Step-by-step submission process</li>
                              <li>Tips for successful nominations</li>
                              <li>How to track your application status</li>
                            </ul>
                          </div>

                          <div class="info-section mt-4">
                            <h4 class="info-title">Quick Actions</h4>
                            <div class="action-buttons">
                              <v-btn
                                color="#FF6B6B"
                                text
                                small
                                @click="openVideoInNewTab"
                                class="action-btn"
                              >
                                <v-icon left size="16">mdi-open-in-new</v-icon>
                                Watch on YouTube
                              </v-btn>
                              <v-btn
                                color="#3A67B1"
                                text
                                small
                                @click="scrollToNominations"
                                class="action-btn"
                              >
                                <v-icon left size="16">mdi-arrow-down</v-icon>
                                Go to Nominations
                              </v-btn>
                            </div>
                          </div>

                          <div class="video-stats">
                            <div class="stat-item">
                              <v-icon small color="#666" class="mr-1">mdi-clock</v-icon>
                              <span class="stat-text">Duration: 5-10 minutes</span>
                            </div>
                            <div class="stat-item">
                              <v-icon small color="#666" class="mr-1">mdi-account-group</v-icon>
                              <span class="stat-text">Beginner friendly</span>
                            </div>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-expand-transition>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Professional Information Sections -->
        <div class="info-sections-container" v-if="!LoadingFlag">
          <v-row>
            <!-- Important Requirements Section -->
            <v-col cols="12" lg="6">
                            <v-card class="info-card requirements-card" elevation="2">
                <v-card-title class="info-card-header">
                  <v-icon color="#0097D7" size="24" class="mr-3">mdi-alert-circle</v-icon>
                  <span class="info-card-title">Important Guidelines</span>
                </v-card-title>
                <v-card-text class="info-card-content">
                  <div class="requirements-list">
                    <div class="requirement-item" v-for="(requirement, index) in importantRequirements" :key="index">
                      <div class="requirement-number">{{ index + 1 }}</div>
                      <div class="requirement-text">{{ requirement }}</div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Application Process Section -->
            <v-col cols="12" lg="6">
              <v-card class="info-card process-card" elevation="2">
                <v-card-title class="info-card-header">
                  <v-icon color="#0097D7" size="24" class="mr-3">mdi-clipboard-list</v-icon>
                  <span class="info-card-title">Application Process</span>
                </v-card-title>
                <v-card-text class="info-card-content">
                  <div class="process-steps">
                    <div class="process-step" v-for="(step, index) in applicationProcess" :key="index">
                      <div class="step-number">{{ index + 1 }}</div>
                      <div class="step-text">{{ step }}</div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <custom-skeleton-loader :LoadingFlag="LoadingFlag" />

        <!-- Card View -->
        <v-row wrap v-if="!LoadingFlag">
          <v-col align="center" cols="12" md="12">
            <v-data-iterator
              :items="tableData1"
              :items-per-page="-1"
              hide-default-footer
            >
              <template v-slot:default="props">
                <v-row dense>
                  <v-col
                    v-for="item in props.items"
                    :key="item.NominationMemberId"
                    cols="12"
                    sm="6"
                    md="4"
                    lg="4"
                  >
                    <v-card
                      class="nomination-card d-flex flex-column justify-space-between"
                      style="height: 100%"
                      elevation="6"
                    >
                      <!-- Professional Header -->
                      <v-card-title
                        class="nomination-header pa-4 white--text"
                        style="background: linear-gradient(135deg, #3A67B1 0%, #0A0F29 100%); border-radius: 8px 8px 0 0;"
                      >
                        <div class="text-center" style="width: 100%;">
                          <!-- <v-icon size="28" color="white" class="mb-2">mdi-account-tie</v-icon> -->
                          <div class="text-h6 font-weight-bold text-center">
                            {{ item.DesignationTypeTxt }}
                          </div>
                        </div>
                      </v-card-title>

                      <!-- Card Content -->
                      <v-card-text class="pa-4 flex-grow-1" style="background-color: #ffffff;">
                        <div class="nomination-details">
                          <!-- Designation Info -->
                          <div class="info-item mb-3">
                            <div class="info-label">
                              <v-icon size="18" color="#3A67B1" class="mr-2">mdi-badge-account</v-icon>
                              <span class="label-text">Designation</span>
                            </div>
                            <div class="info-value">{{ item.NominationDesignationName }}</div>
                          </div>

                          <!-- Fee Info -->
                          <div class="info-item mb-3">
                            <div class="info-label">
                              <v-icon size="18" color="#56BDA3" class="mr-2">mdi-currency-inr</v-icon>
                              <span class="label-text">Nomination Fee</span>
                            </div>
                            <div class="info-value fee-amount">{{ item.NominationFeeTxt }}</div>
                          </div>

                          <!-- Status Section (Only if application exists) -->
                          <div v-if="item.NominationId > 0" class="status-section mt-4 pt-3" style="border-top: 1px solid #e0e0e0;">
                            <div class="status-title mb-3">
                              <v-icon size="18" color="#666" class="mr-2">mdi-clipboard-check</v-icon>
                              <span class="text-subtitle-2 font-weight-medium" style="color: #666;">Application Status</span>
                            </div>

                            <!-- Application Status -->
                            <div class="status-item mb-2">
                              <span class="status-label">Application:</span>
                              <v-chip
                                small
                                :color="item.NominationStatusColor"
                                class="white--text ml-2"
                                dark
                              >
                                {{ item.NominationStatusText }}
                              </v-chip>
                            </div>

                            <!-- LO Certification -->
                            <div class="status-item mb-2">
                              <span class="status-label">LO Certification:</span>
                              <v-chip
                                small
                                :color="item.PresidentApprovalStatusColor"
                                class="white--text ml-2"
                                dark
                              >
                                {{ item.PresidentApprovalStatusText }}
                              </v-chip>
                            </div>

                            <!-- NHQ Status -->
                            <div class="status-item">
                              <span class="status-label">NHQ Status:</span>
                              <v-chip
                                small
                                :color="item.NhqApprovalStatusColor"
                                class="white--text ml-2"
                                dark
                              >
                                {{ item.NhqApprovalStatusText }}
                              </v-chip>
                            </div>
                          </div>
                        </div>
                      </v-card-text>
                      <!-- Professional Action Buttons -->
                      <v-card-actions class="pa-4" style="background-color: white; border-top: 1px solid #414042;">
                        <v-row no-gutters justify="center" class="action-buttons-row">
                          <!-- Apply Button - Enabled State -->
                          <v-col cols="auto" class="mx-1 mb-2" v-if="item.NominationCreateFlag">
                            <v-btn
                              color="#56BDA3"
                              class="action-btn white--text"
                              elevation="2"
                              small
                              @click="addNominationData(item)"
                            >
                              <v-icon left size="16" color="white">mdi-clipboard-check</v-icon>
                              Apply
                            </v-btn>
                          </v-col>

                          <!-- Apply Button - Disabled State -->
                          <v-col cols="auto" class="mx-1 mb-2" v-if="!item.EligibleFlag && !item.NominationCreateFlag">
                            <v-btn
                              color="#0097D7"
                              class="action-btn white--text"
                              elevation="2"
                              small
                              @click="handleApplyClick(item)"
                            >
                              <v-icon left size="16" color="white">mdi-clipboard-check</v-icon>
                              Apply
                            </v-btn>
                          </v-col>

                          <v-col cols="auto" class="mx-1 mb-2" v-if="!item.NominationEditFlag && item.FloorNominationFlag && item.FloorNominationCreateFlag">
                            <v-btn
                              color="warning"
                              class="action-btn white--text"
                              elevation="2"
                              small
                              @click="addFloorNominationData(item)"
                            >
                              <v-icon left size="16" color="white">mdi-account-plus</v-icon>
                              Floor Nomination
                            </v-btn>
                          </v-col>

                          <v-col cols="auto" class="mx-1 mb-2" v-if="!item.EligibleFlag && !item.NominationCreateFlag && ShowFloorNomination(item)">
                             <v-btn
                              color="#FDDA0D"
                              class="action-btn black--text"
                              elevation="2"
                              small
                              @click="handleFloorNominationClick(item)"
                            >
                              <v-icon left size="16" color="black">mdi-account-plus</v-icon>
                              Floor Nomination
                            </v-btn>
                          </v-col>

                          <!-- Co-option Apply Button -->
                           <v-col cols="auto" class="mx-1 mb-2" v-if="!item.NominationEditFlag && item.CoOptionOpenFlag">
                            <v-btn
                              color="deep-purple darken-1"
                              class="action-btn white--text"
                              elevation="2"
                              small
                              @click="addCoOptionNominationData(item)"
                            >
                              <v-icon left size="16" color="white">mdi-account-plus</v-icon>
                              Co-option
                            </v-btn>
                          </v-col>

                          <!-- Co-option Error/Disabled Button -->
                          <v-col cols="auto" class="mx-1 mb-2" v-if="!item.CoOptionErrorFlag && !item.NominationCreateFlag && item.NominationDesignationId == 4 && !item.CoOptionOpenFlag">
                            <v-btn
                              color="#b39ddb"
                              class="action-btn black--text"
                              elevation="2"
                              small
                              @click="handleCoOptionClick(item)"
                            >
                              <v-icon left size="16" color="black">mdi-alert-circle-outline</v-icon>
                              Co-option
                            </v-btn>
                          </v-col>

                          <!-- Edit Button -->
                          <v-col cols="auto" class="mx-1 mb-2" v-if="item.NominationEditFlag">
                            <v-btn
                              @click="editData(item)"
                              color="warning"
                              class="action-btn white--text"
                              elevation="2"
                              small
                            >
                              <v-icon left size="16" color="white">mdi-pencil</v-icon>
                              Edit
                            </v-btn>
                          </v-col>

                          <!-- Download Button -->
                          <v-col cols="auto" class="mx-1 mb-2" v-if="item.NominationDownloadFlag">
                            <v-btn
                              color="success"
                              class="action-btn white--text"
                              :href="item.FilePath"
                              target="_blank"
                              elevation="2"
                              small
                            >
                              <v-icon left size="16" color="white">mdi-download</v-icon>
                              Download
                            </v-btn>
                          </v-col>

                          <!-- Eligibility Button -->
                          <v-col cols="auto" class="mx-1 mb-2">
                            <v-btn
                              color="brown"
                              class="action-btn white--text"
                              elevation="2"
                              small
                              @click="showPreviewPlanPopup(item)"
                            >
                              <v-icon left size="16" color="white">mdi-view-list</v-icon>
                              Eligibility
                            </v-btn>
                          </v-col>

                          <!-- Pay Now Button -->
                          <v-col cols="auto" class="mx-1 mb-2" v-if="item.PaymentButtonFlag">
                            <v-btn
                              color="success"
                              class="action-btn white--text"
                              elevation="2"
                              small
                              @click="confirmAlert(item)"
                            >
                              <v-icon left size="16" color="white">mdi-credit-card</v-icon>
                              Pay Now
                            </v-btn>
                          </v-col>
                        </v-row>
                      </v-card-actions>
                    </v-card>
                  </v-col>
                </v-row>
              </template>
            </v-data-iterator>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>


<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import EligibilityDetailsPopup from "@/view/pages/erp/events/nominate-member/EligibilityDetailsPopup.vue";
import NominationDashboardCards from "@/view/components/nomination/NominationDashboardCards.vue";

export default {
  components: {
    EligibilityDetailsPopup,
    NominationDashboardCards
  },
  mixins: [common],
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      StartupLoadingFlag: false,
      FloatingButtonFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      ResultFlag: false,
      OverlayLoadingFlag: false,

      PreviewPlanFlag: false,
      PreviewPlanDetails: {},

      // Video Tutorial Properties
      showVideoSection: false,
      youtubeVideoId: "rCDoZvzBQqc",

      rows: [],
      tableColumns1: [],
      selected: [],
      search: "",
      CurrentYearId: 0,

      CompletedDateFlag: false,

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      selectedData: {},

      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,

      TableDisplayFlag1: false,

      search1: "",
      selected1: "",
      tableColumns1: [],
      tableData1: [],
      tableOptions1: {},

      tableColumns2: [],
      tableData2: [],
      tableOptions2: {},

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
      Order: {},
      DownloadFlag: false,
      ExcelFileName: "",
      ExcelFields: {},

      // Dashboard Data - Single object that will be populated from API
      dashboardData: {
        loading: false,
        cardTitles: {
          memberCard: 'Member Information',
          loCard: 'LO Information',
          deadlineCard: 'Nomination Deadline',
          statisticsCard: 'Application Statistics'
        },
        memberInfo: {
          name: "Member Name",
          membershipId: "JCI-12345",
          profilePhoto: "",
          mobileVerified: false,
          aadharVerified: false,
          continuousYears: 0
        },
        loInfo: {
          name: "JCI Local Organization",
          zone: "Zone Name",
          city: "City Name",
          district: "District Name",
          state: "State Name",
          status: "Active",
          votingCount: 0
        },
        deadlineInfo: {
          startDate: "15-Aug-2025",
          endDate: "30-Sep-2025",
          timeRemaining: "15 days, 6 hours"
        },
        statisticsInfo: {
          total: 0,
          submitted: 0,
          pending: 0,
          type: 'Applications'
        }
      },
      FloorNominationFlag:false,

      // Professional Information Data
      importantRequirements: [
        "The nominee should have updated their profile information and OTP verified their Aadhaar",
        "The nominee should have completed desired posting to apply for the next post",
        "The nominee should have continuous membership as prescribed in the constitution",
        "The applications should be final submitted within the nomination deadline",
        "The nomination fee is non-refundable"
      ],
      applicationProcess: [
        "First save the application with your information",
        "Pay the nomination fee as applicable",
        "Later, final submit the application",
        "Application once final submitted cannot be edited again",
        "Max file size per PDF should not exceed 1 MB"
      ],

      NominationInfoLoadingFlag: false,

      memberDataFlag: false,
      memberData: {},
    };
  },
  mounted() {
    /*
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Vuetify", route: "alerts" },
      { title: "Form Inputs & Control", route: "autocompletes" },
      { title: "Fileinptus" },
    ]);
    */
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomCode = LomId;

        this.pageData();
      }
    },
    SearchId: function () {
      console.log("watch SearchId");
      this.rows = [];
    },
  },
  computed: {
    youtubeEmbedUrl() {
      return `https://www.youtube.com/embed/${this.youtubeVideoId}?rel=0&modestbranding=1&showinfo=0`;
    }
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "nomination_designation",
        Action: "list_1",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    // Video Tutorial Methods
    toggleVideoSection() {
      this.showVideoSection = !this.showVideoSection;
    },
    openVideoInNewTab() {
      window.open(`https://youtu.be/${this.youtubeVideoId}`, '_blank');
    },
    scrollToNominations() {
      this.$nextTick(() => {
        const nominationsElement = document.querySelector('.nomination-card');
        if (nominationsElement) {
          nominationsElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    },
    ShowFloorNomination(item) {
      if (item.DesignationType == 1 && item.NominationDesignationId == 3 && item.FloorNominationZpFlag) {
        return true;
      }
      if (item.DesignationType == 1 && item.NominationDesignationId == 4 && item.FloorNominationZvpFlag) {
        return true;
      }
    },
    handleApplyClick(item) {
      var designationType = item.DesignationType;
      if (designationType == 1 && item.NominationZoneDeadlineError) {
        this.sweetAlert('error', item.NominationZoneDeadlineError, false);
        return;
      } else if (designationType != 1 && item.NominationNationalDeadlineError) {
        this.sweetAlert('error', item.NominationNationalDeadlineError, false);
        return;
      }
      if (item.EligibleErrorTxt) {
        this.sweetAlert('error', item.EligibleErrorTxt, false);
        return;
      }
    },
    handleFloorNominationClick(item) {
      var designationType = item.DesignationType;
      if (designationType == 1 && item.FloorNominationZoneError) {
        this.sweetAlert('error', item.FloorNominationZoneError, false);
        return;
      } else if (designationType != 1 && item.FloorNominationNationalError) {
        this.sweetAlert('error', item.FloorNominationNationalError, false);
        return;
      }
      if (item.EligibleErrorTxt) {
        this.sweetAlert('error', item.EligibleErrorTxt, false);
        return;
      }
    },
    handleCoOptionClick(item) {
      console.log("handleCoOptionClick called");
      if (item.CoOptionDeadlineError) {
        this.sweetAlert('error', item.CoOptionDeadlineError, false);
        return;
      }
      if (item.EligibleErrorTxt) {
        this.sweetAlert('error', item.EligibleErrorTxt, false);
        return;
      }
    },
    checkout() {
      console.log("checkout");
      this.pageRedirect({}, "/members/subscription/payment/checkout");
    },
    pageData() {
      console.log("pageData called");
      this.searchForm();
      this.getNominationInfo();
    },
    showPreviewPlanPopup(tr) {
      this.PreviewPlanDetails = tr;
      this.PreviewPlanFlag = true;
    },
    hidePreviewPlanPopup() {
      this.PreviewPlanDetails = {};
      this.PreviewPlanFlag = false;
    },
    getActiveStatusColor(status) {
      // Method to get color for status chips
      switch (status) {
        case 'Active':
          return '#28a745';
        case 'Inactive':
          return '#dc3545';
        case 'Pending':
          return '#ffc107';
        default:
          return '#6c757d';
      }
    },
    getApplicationCount(status) {
      // Method to count applications by status
      if (!this.tableData1 || !Array.isArray(this.tableData1)) {
        return 0;
      }
      return this.tableData1.filter(item => {
        if (status === 'Submitted') {
          return item.NominationStatusText === 'Submitted' || item.NominationStatusText === 'Approved';
        } else if (status === 'Pending') {
          return item.NominationStatusText === 'Pending' || item.NominationStatusText === 'Draft';
        }
        return false;
      }).length;
    },
    addNominationData(tr) {
      console.log("addNominationData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      
      // Determine Nomination Type
      var NominationType = this.FloorNominationFlag ? 2 : 1; // 1 = Normal, 2 = Floor Nomination
      console.log("NominationType=" + NominationType);

      if (n1 > 0) {
        var url = "/nominate-member/nomination-submit-form";
        console.log({ url });

        var params = {
          v1: tr.NominationDesignationId,
          v2: tr.NominationId,
          v3: tr.NominationMemberId,
          v4: NominationType
        };
        this.pageRedirect(params, url);
      } else {
        var message = "Kindly select one LO to send report";
        this.sweetAlert("error", message, false);
      }
    },
    addCoOptionNominationData(tr) {
      console.log("addCoOptionNominationData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      // Determine Nomination Type
      var NominationType = 3; // 3 = Co-Option Nomination
      console.log("NominationType=" + NominationType);

      if (n1 > 0) {
        var url = "/nominate-member/nomination-submit-form";
        console.log({ url });

        var params = {
          v1: tr.NominationDesignationId,
          v2: tr.NominationId,
          v3: tr.NominationMemberId,
          v4: NominationType,
        };
        this.pageRedirect(params, url);
      } else {
        var message = "Kindly select one LO to send report";
        this.sweetAlert("error", message, false);
      }
    },
    addFloorNominationData(tr) {
      console.log("addFloorNominationData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      
      this.FloorNominationFlag = true;
      // Determine Nomination Type
      var NominationType = this.FloorNominationFlag ? 2 : 1; // 1 = Normal, 2 = Floor Nomination
      console.log("NominationType=" + NominationType);

      if (n1 > 0) {
        var url = "/nominate-member/nomination-submit-form";
        console.log({ url });

        var params = {
          v1: tr.NominationDesignationId,
          v2: tr.NominationId,
          v3: tr.NominationMemberId,
          v4: NominationType
        };
        this.pageRedirect(params, url);
      } else {
        var message = "Kindly select one LO to send report";
        this.sweetAlert("error", message, false);
      }
    },
    searchForm() {
      console.log("searchForm called");

      // var validate1 = this.$refs.form2.validate();
      var validate1 = true;
      console.log("validate1=" + validate1);

      if (validate1) {
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/nomination-designation/lists";
        var upload = {
          UserInterface: 1,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        this.tableData1 = [];
        this.LoadingFlag = true;
        this.SearchFlag = true;

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.LoadingFlag = false;
            thisIns.SearchFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            var options = records.TableOptions;
            thisIns.tableOptions1 = options;

            if (flag == 1) {
              thisIns.tableData1 = records.TableData;
              // thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            // thisIns.toast("error", error);
            thisIns.LoadingFlag = false;
            thisIns.SearchFlag = false;
          });
      } else {
        var message = "Kindly fill required fields.";
        this.toast("error", message);
      }
    },
    editData(tr) {
      console.log("editData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        var url = "/nominate-member/nomination-edit-form";

        var params = {
          v1: tr.NominationDesignationId,
          v2: tr.NominationId,
          v3: tr.NominationMemberId,
        };
        this.pageRedirect(params, url);
      } else {
        var message = "Kindly select one LO to send report";
        this.sweetAlert("error", message, false);
      }
    },
    confirmAlert(item) {
      var TotalAmount = item.NominationFee;
      console.log("TotalAmount=" + TotalAmount);

      // TotalAmount=0;

      if (TotalAmount > 0) {
        Swal.fire({
          title: "Do you want to continue?",
          text: "Please verify all the information before proceeding",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Apply`,
          confirmButtonColor: "#56BDA3",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#414042",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.submitForm(item);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields. ";
        }
        if (TotalAmount == 0) {
          message += "Minimum one nos required to continue. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    getNominationInfo() {
      console.log("getNominationInfo called");

      // Prevent multiple simultaneous calls
      if (this.NominationInfoLoadingFlag) {
        console.log("getNominationInfo already loading, skipping...");
        return;
      }

      this.memberDataFlag = false;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/nomination-member/statistics-info";
      var upload = {
        UserInterface: 1,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = {};
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("getNominationInfo response=" + JSON.stringify(response));

          output = response.data.output;
          flag = response.data.flag;
          console.log("output=" + output + ", flag=" + flag);

          records = response.data.records;
          console.log("nomination info records:", { records });

          thisIns.memberDataFlag = flag;

          if (flag == 1) {
            // Update NominationInfo with API response
            thisIns.memberData = records
          }
        })
        .catch(function (error) {
          console.log("getNominationInfo error=" + error);
        });
    },
    submitForm(item) {
      console.log("submitForm is called");
      console.log(item);

      var NominationDesignationId = item.NominationDesignationId;

      // TotalAmount=0;

      if (NominationDesignationId > 0) {
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/nomination-order/create";
        var upload = {
          UserInterface: 1,
          NominationDesignation: NominationDesignationId,
          Designation: item.DesignationId,
          Amount: item.NominationFee,
        };

        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        // this.$session.set("checkout", upload);
        // this.checkout();

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.Order = records.Order;
              thisIns.makePayment();
            } else {
              // thisIns.toast("error", output);
              thisIns.sweetAlert("info", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        var message = "";
        if (TotalAmount == 0) {
          message += "Minimum one nos required to continue. ";
        }
        this.sweetAlert("error", message, false);
        // this.toast("error", message);
      }
    },
    makePayment() {
      console.log("makePayment");
      // ev.preventDefault();
      var order = this.Order;
      console.log({ order });
      const thisIns = this;
      var rzp1 = new Razorpay({
        key: order.rzp_key,
        account_id: order.rzp_account_id,
        order_id: order.rzp_order_id,
        amount: order.rzp_amount,
        name: order.rzp_name,
        currency: "INR",
        description: order.rzp_description,
        image: order.rzp_image,
        theme: {
          color: order.rzp_color,
        },
        prefill: {
          name: order.customer_name,
          email: order.customer_email_id,
          contact: order.customer_mobile_no,
        },
        notes: {
          address: "",
        },
        // callback_url: "http://domain.com/#/about/" + this.order_id,
        redirect: false,
        handler: function (response) {
          console.log("response=" + JSON.stringify(response));
          console.log("razorpay_payment_id=" + response.razorpay_payment_id);
          thisIns.SubmitFlag = true;
          thisIns.searchForm();
          // thisIns.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
          // thisIns.pageRedirect({}, "/order/success");
        },
      });
      rzp1.open();
    },

    // Dashboard Card Methods
    getDateStatus(type) {
      // Sample logic - will be updated with actual date comparison
      const now = new Date();
      if (type === 'start') {
        return now >= new Date('2025-08-15') ? 'Active' : 'Upcoming';
      } else {
        return now <= new Date('2025-09-30') ? 'Active' : 'Expired';
      }
    },

    showDeadlineDetails(type) {
      console.log(`Showing ${type} deadline details`);
      const message = type === 'start'
        ? `Nomination period starts on ${this.dashboardData.deadlineInfo.startDate}`
        : `Nomination period ends on ${this.dashboardData.deadlineInfo.endDate}`;

      this.sweetAlert("info", message, false);
    },

    showNominationStats(type) {
      console.log(`Showing ${type} nomination statistics`);
      let message = "";
      if (type === 'total') {
        message = `Total of ${this.totalNominations} nominations have been submitted. Progress: ${this.nominationProgress}%`;
      } else {
        message = `Status Overview:\n• Overall: ${this.overallNominations}\n• Pending: ${this.pendingCount}\n• Submitted: ${this.submittedCount}`;
      }
      this.sweetAlert("info", message, false);
    },

    showZoneStats() {
      console.log("Showing zone statistics");
      this.sweetAlert("info", `Your zone has submitted ${this.zoneNominations} nominations so far.`, false);
    },

    showPaymentStats() {
      console.log("Showing payment statistics");
      this.sweetAlert("info", `${this.paidNominations} out of ${this.totalNominations} nominations have completed payment.`, false);
    },

    // New Dashboard Card Methods
    getActiveStatusColor(status) {
      // Method to get color for status chips
      switch (status) {
        case 'Active':
          return '#28a745';
        case 'Inactive':
          return '#dc3545';
        case 'Pending':
          return '#ffc107';
        default:
          return '#6c757d';
      }
    },

    getApplicationCount(status) {
      // Method to count applications by status
      if (!this.tableData1 || this.tableData1.length === 0) {
        return 0;
      }

      return this.tableData1.filter(item => {
        // Adjust this based on your actual data structure
        return item.NominationStatusTxt === status ||
               item.StatusTxt === status ||
               item.ApplicationStatus === status;
      }).length;
    },

    handleImageError(event) {
      // Handle member profile image error
      console.log("Image failed to load, using fallback");
      event.target.style.display = 'none';
    },

    showTimeRemaining() {
      console.log("Showing time remaining");
      this.sweetAlert("info", `${this.dashboardData.deadlineInfo.timeRemaining} remaining until nomination deadline.`, false);
    },
  },
  beforeMount() {
    var token = this.$session.get("token");
    token = token == (null || undefined) ? "" : token;
    console.log("nomination token=" + token);

    if (token == "") {
      var path = this.$route.fullPath;
      console.log("path=" + path);
      console.log("The user is unauthorised");
      this.$store.dispatch("user/setRedirectUrl", path);
      this.LogoutUser();
      return;
    }
    this.$store.dispatch("user/setRedirectUrl", "");
    console.log("Authorised user");
    this.refreshPageData();

    this.tableColumns1.push(
      { text: "Paid Date", value: "PaidDateTxt" },
      { text: "Year", value: "YearName" },
      {
        text: "Subscription Description",
        value: "ContributionTypeDescription",
      },
      { text: "Paid Count", value: "PaidCount" },
      { text: "Remaining Count", value: "RemainingCount" },
      { text: "Add members", value: "actions" }
    );
  },
};
</script>
<style lang="scss">
/* This is for documentation purposes and will not be needed in your application */

@import "~vue-tabulator/dist/scss/bootstrap/tabulator_bootstrap4";

#ldmt-lom-status-list {
  background-color: #ffffff;
  min-height: 100vh;

  .main-container {
    border-radius: 12px !important;
    overflow: hidden;
    background: white;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08) !important;
  }

  // Professional Header Styles
  .professional-header {
    background: linear-gradient(135deg, #ffffff 0%, #ffffff 100%);
    border-bottom: 1px solid #414042;

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      width: 100%;

      .title-section {
        flex: 1;

        .main-title-wrapper {
          display: flex;
          align-items: center;

          .title-text {
            .page-main-title {
              font-size: 2rem;
              font-weight: 700;
              color: #0A0F29;
              margin: 0;
              font-family: 'Roboto', sans-serif;
              line-height: 1.2;
            }

            .year-badge {
              margin-top: 0.5rem;
            }
          }
        }
      }

      .action-section {
        .refresh-btn {
          font-weight: 500;
          text-transform: none;
          padding: 0 24px;
          height: 40px;

          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(86, 189, 163, 0.3);
          }
        }
      }
    }

    .subtitle-section {
      .subtitle-wrapper {
        display: flex;
        align-items: center;
        background: rgba(58, 103, 177, 0.05);
        padding: 1rem;
        border-radius: 8px;
        border-left: 4px solid #3A67B1;

        .page-description {
          font-size: 1.1rem;
          color: #414042;
          margin: 0;
          font-weight: 400;
          line-height: 1.4;
        }
      }
    }
  }

  // Professional Content Styles
  .main-content {
    background: #ffffff;
  }

  // Professional Loading Styles
  .loading-section {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;

    .loading-wrapper {
      text-align: center;

      .loading-text {
        font-size: 1.1rem;
        color: #414042;
        font-weight: 500;
        margin: 0;
      }
    }
  }

  // Dashboard Cards Section
  .dashboard-section {
    margin: 2rem 0;

    .dashboard-cards {
      margin: 0 -12px;

      .dashboard-card {
        cursor: pointer;
        transition: all 0.3s ease;
        border-radius: 12px !important;
        background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
        height: 100%;
        display: flex;
        flex-direction: column;

        .v-card__text {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        &:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
        }

        .card-title {
          color: #2c3e50;
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 1rem;
          font-family: 'Roboto', sans-serif;
        }

        &.member-info-card {
          border-left: 4px solid #3A67B1;

          .member-avatar-section {
            .member-avatar {
              border: 3px solid #3A67B1 !important;
              box-shadow: 0 4px 12px rgba(58, 103, 177, 0.3) !important;

              img {
                width: 100%;
                height: 100%;
                object-fit: cover;
              }
            }
          }

          .member-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 8px;

            .info-item {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 12px 16px;
              background: rgba(58, 103, 177, 0.1);
              border-radius: 8px;
              border: 1px solid rgba(58, 103, 177, 0.2);
              transition: all 0.2s ease;

              &:hover {
                background: rgba(58, 103, 177, 0.15);
                transform: translateX(2px);
              }

              .info-label {
                color: #3A67B1;
                font-weight: 600;
                font-size: 0.85rem;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                white-space: nowrap;
              }

              .info-value {
                color: #2c3e50;
                font-weight: 600;
                font-size: 0.9rem;
                text-align: right;
                word-break: break-word;
              }

              .status-chip {
                font-weight: 600 !important;
                font-size: 0.75rem !important;
                height: 24px !important;

                .v-icon {
                  margin-right: 2px !important;
                }
              }

              .subscription-info {
                display: flex;
                flex-direction: column;
                align-items: flex-end;
                text-align: right;

                .subscription-count {
                  color: #3A67B1;
                  font-weight: 700;
                  font-size: 1.1rem;
                  line-height: 1;
                }

                .subscription-label {
                  color: #666;
                  font-weight: 500;
                  font-size: 0.75rem;
                  text-transform: uppercase;
                  letter-spacing: 0.3px;
                }
              }
            }
          }
        }

        &.lo-info-card {
          border-left: 4px solid #56BDA3;

          .lo-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 8px;

            .info-item {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 12px 16px;
              background: rgba(86, 189, 163, 0.1);
              border-radius: 8px;
              border: 1px solid rgba(86, 189, 163, 0.2);
              transition: all 0.2s ease;

              &:hover {
                background: rgba(86, 189, 163, 0.15);
                transform: translateX(2px);
              }

              .info-label {
                color: #56BDA3;
                font-weight: 600;
                font-size: 0.9rem;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }

              .info-value {
                color: #2c3e50;
                font-weight: 600;
                font-size: 0.9rem;
              }

              .status-chip {
                font-weight: 600 !important;
                font-size: 0.8rem !important;
              }
            }
          }
        }

        &.deadline-card {
          border-left: 4px solid #0097D7;

          .deadline-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 8px;

            .date-item {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 12px 16px;
              background: rgba(0, 151, 215, 0.1);
              border-radius: 8px;
              border: 1px solid rgba(0, 151, 215, 0.2);
              transition: all 0.2s ease;

              &:hover {
                background: rgba(0, 151, 215, 0.15);
                transform: translateX(2px);
              }

              .date-label {
                color: #0097D7;
                font-weight: 600;
                font-size: 0.9rem;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }

              .date-value {
                color: #2c3e50;
                font-weight: 600;
                font-size: 0.9rem;
              }
            }

            .time-remaining {
              text-align: center;
              margin-top: 8px;

              .time-chip {
                font-weight: 600 !important;
                font-size: 0.8rem !important;
              }
            }
          }
        }

        &.statistics-card {
          border-left: 4px solid #EDBE38;

          .stats-info {
            display: flex;
            flex-direction: column;
            gap: 12px;
            flex: 1;

            .stat-item {
              text-align: center;
              padding: 16px 12px;
              background: rgba(237, 190, 56, 0.1);
              border-radius: 8px;
              border: 1px solid rgba(237, 190, 56, 0.2);
              transition: all 0.2s ease;

              &:hover {
                background: rgba(237, 190, 56, 0.15);
                transform: translateY(-1px);
              }

              .stat-number {
                display: block;
                font-size: 1.8rem;
                font-weight: 700;
                color: #EDBE38;
                line-height: 1;
                margin-bottom: 4px;
              }

              .stat-label {
                display: block;
                font-size: 0.85rem;
                color: #666;
                font-weight: 500;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
            }
          }
        }
      }
    }
  }

  // Professional Info Cards Styles
  .info-sections-container {
    margin: 2rem 0;

    .info-card {
      border-radius: 12px !important;
      border: 1px solid #e0e0e0;
      transition: all 0.3s ease;
      height: 100%;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0,0,0,0.1) !important;
      }

      .info-card-header {
        background: linear-gradient(135deg, #fff 0%, #ffffff 100%);
        border-bottom: 1px solid #414042;
        padding: 1.5rem;

        .info-card-title {
          font-size: 1.2rem;
          font-weight: 600;
          color: #0A0F29;
          margin: 0;
        }
      }

      .info-card-content {
        padding: 1.5rem;
      }

      &.requirements-card {
        .info-card-header {
          border-left: 4px solid #0097D7;
        }
      }

      &.process-card {
        .info-card-header {
          border-left: 4px solid #0097D7;
        }
      }
    }
  }

  // Requirements List Styles
  .requirements-list {
    .requirement-item {
      display: flex;
      align-items: flex-start;
      margin-bottom: 1rem;
      padding: 1rem;
      background: rgba(0, 151, 215, 0.05);
      border-radius: 8px;
      border-left: 3px solid #0097D7;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(0, 151, 215, 0.1);
        transform: translateX(4px);
      }

      .requirement-number {
        background: #0097D7;
        color: white;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.8rem;
        font-weight: 600;
        margin-right: 1rem;
        flex-shrink: 0;
      }

      .requirement-text {
        font-size: 0.95rem;
        color: #414042;
        line-height: 1.6;
        font-weight: 400;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  // Process Steps Styles
  .process-steps {
    .process-step {
      display: flex;
      align-items: flex-start;
      margin-bottom: 1rem;
      padding: 1rem;
      background: rgba(0, 151, 215, 0.05);
      border-radius: 8px;
      border-left: 3px solid #0097D7;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(0, 151, 215, 0.1);
        transform: translateX(4px);
      }

      .step-number {
        background: #0097D7;
        color: white;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.8rem;
        font-weight: 600;
        margin-right: 1rem;
        flex-shrink: 0;
      }

      .step-text {
        font-size: 0.95rem;
        color: #414042;
        line-height: 1.6;
        font-weight: 400;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  .card-title {
    font-size: 25px;
    font-family: "Lora", serif;
    padding: 20px;
    text-align: center;
    word-wrap: break-word;
  }
  .card-subtitle {
    font-size: 60px;
    font-family: "Lobster", cursive;
    color: white;
    text-align: center;
  }
  .card-subtext {
    font-size: 40px;
    font-family: "Roboto", cursive !important;
    color: white;
    text-align: center;
  }
  .notes-text1 {
    padding: 5px;
    margin-left: 2rem;
    font-size: 18px;
    font-family: sans-serif;
  }

  .notes-section {
    margin: 1.5rem 0;
    padding: 1.5rem;
    background-color: rgba(58, 103, 177, 0.05);
    border-left: 4px solid #3A67B1;
    border-radius: 8px;
  }

  .notes-title {
    color: #0A0F29;
    font-family: "Roboto", sans-serif;
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 1rem;
    text-transform: none;
    letter-spacing: 0.5px;
  }

  .notes-list {
    margin: 0;
    padding-left: 1.8rem;

    li {
      font-family: "Arial", sans-serif;
      font-size: 1rem;
      line-height: 1.8;
      margin-bottom: 0.8rem;
      color: #414042;
      padding-left: 0.5rem;

      &:last-child {
        margin-bottom: 0;
      }

      &:hover {
        color: #007bff;
        transition: color 0.2s ease;
      }
    }
  }

  // Dashboard Cards Styles
  .dashboard-cards-section {
    margin: 2rem 0;
    padding: 1rem 0;
  }

  .dashboard-row {
    margin-bottom: 1rem;
  }

  .dashboard-card {
    cursor: pointer;
    transition: all 0.3s ease;
    border-radius: 12px !important;
    background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
    }

    .card-title {
      color: #2c3e50;
      font-size: 1rem;
      font-weight: 600;
      margin-bottom: 0.5rem;
      font-family: 'Roboto', sans-serif;
    }

    .card-date {
      color: #495057;
      font-size: 1.2rem;
      font-weight: bold;
      margin-bottom: 0.25rem;
    }

    .card-time {
      color: #6c757d;
      font-size: 0.9rem;
      margin-bottom: 0.5rem;
    }

    .card-number {
      color: #007bff;
      font-size: 2rem;
      font-weight: bold;
      margin-bottom: 0.25rem;
      font-family: 'Arial', sans-serif;
    }

    .card-subtitle {
      color: #6c757d;
      font-size: 0.85rem;
      margin-bottom: 0.5rem;
    }
  }

  .deadline-card {
    border-left: 4px solid #28a745;

    &:nth-child(2) {
      border-left-color: #dc3545;
    }
  }

  .stats-card {
    border-left: 4px solid #007bff;

    &:nth-child(4) {
      border-left-color: #ffc107;
    }
  }

  .status-indicators {
    .v-chip {
      font-size: 0.7rem;
      height: 20px;
    }
  }

  .info-cards-row {
    margin-top: 1rem;
  }

  .info-card {
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 8px !important;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
    }

    .info-title {
      color: #2c3e50;
      font-size: 1rem;
      font-weight: 600;
      margin-bottom: 0.25rem;
    }

    .info-text {
      color: #6c757d;
      font-size: 0.9rem;
      margin-bottom: 0;
    }
  }

  // Responsive adjustments
  @media (max-width: 768px) {
    .dashboard-card {
      margin-bottom: 1rem;

      .card-number {
        font-size: 1.5rem;
      }
    }

    .info-card {
      margin-bottom: 0.5rem;
    }
  }

  // Professional Nomination Cards Styles
  .nomination-card {
    border-radius: 12px !important;
    transition: all 0.3s ease;
    border: 1px solid #e0e0e0;
    background: #ffffff;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(0,0,0,0.12) !important;
      border-color: #1976d2;
    }
  }

  .nomination-header {
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;
      height: 1px;
      background: rgba(255,255,255,0.2);
    }
  }

  .nomination-details {
    .info-item {
      .info-label {
        display: flex;
        align-items: center;
        margin-bottom: 0.5rem;

        .label-text {
          font-size: 0.875rem;
          font-weight: 600;
          color: #424242;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }

      .info-value {
        font-size: 1rem;
        color: #2c3e50;
        font-weight: 500;
        padding-left: 26px;
        line-height: 1.4;

        &.fee-amount {
          color: #388e3c;
          font-weight: 600;
        }
      }
    }
  }

  .status-section {
    .status-title {
      display: flex;
      align-items: center;
    }

    .status-item {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .status-label {
        font-size: 0.875rem;
        color: #666;
        font-weight: 500;
      }
    }
  }

  .action-buttons-row {
    gap: 0.25rem;
  }

  .action-btn {
    min-width: unset !important;
    padding: 0 12px !important;
    font-size: 0.8rem !important;
    font-weight: 500 !important;
    text-transform: none !important;
    letter-spacing: 0.3px !important;
    border-radius: 6px !important;
    transition: all 0.2s ease !important;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
    }

    .v-icon {
      margin-right: 4px !important;
    }
  }

  // Responsive adjustments for nomination cards
  @media (max-width: 768px) {
    .nomination-card {
      margin-bottom: 1rem;
    }

    .action-btn {
      padding: 0 8px !important;
      font-size: 0.75rem !important;

      .v-icon {
        margin-right: 2px !important;
      }
    }

    .action-buttons-row {
      justify-content: center !important;
    }
  }

  @media (max-width: 600px) {
    .nomination-details .info-value {
      padding-left: 20px;
      font-size: 0.9rem;
    }

    .status-item {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;

      .status-label {
        font-size: 0.8rem;
      }
    }
  }

  // Professional Layout Responsive Design
  @media (max-width: 1024px) {
    .professional-header {
      .header-content {
        flex-direction: column;
        gap: 1rem;

        .title-section .main-title-wrapper .title-text .page-main-title {
          font-size: 1.75rem;
        }

        .action-section {
          align-self: flex-start;
        }
      }
    }

    .info-sections-container {
      .info-card {
        margin-bottom: 1.5rem;
      }
    }
  }

  @media (max-width: 768px) {
    .main-container {
      margin: 0.5rem;
      border-radius: 8px !important;
    }

    .professional-header {
      padding: 1rem !important;

      .header-content {
        .title-section .main-title-wrapper {
          flex-direction: column;
          align-items: flex-start;
          gap: 0.75rem;

          .title-text .page-main-title {
            font-size: 1.5rem;
          }
        }

        .subtitle-section .subtitle-wrapper {
          padding: 0.75rem;

          .page-description {
            font-size: 1rem;
          }
        }
      }
    }

    .main-content {
      padding: 1rem !important;
    }

    .requirements-list .requirement-item,
    .process-steps .process-step {
      padding: 0.75rem;

      .requirement-text,
      .step-text {
        font-size: 0.9rem;
      }
    }
  }

  @media (max-width: 480px) {
    .professional-header {
      .header-content .title-section .main-title-wrapper .title-text .page-main-title {
        font-size: 1.3rem;
        line-height: 1.3;
      }
    }

    .requirements-list .requirement-item,
    .process-steps .process-step {
      flex-direction: column;

      .requirement-number,
      .step-number {
        margin-bottom: 0.5rem;
        margin-right: 0;
        align-self: flex-start;
      }
    }

    .info-card .info-card-header {
      padding: 1rem;

      .info-card-title {
        font-size: 1.1rem;
      }
    }

    .info-card .info-card-content {
      padding: 1rem;
    }
  }
}

/* Professional Video Tutorial Styles */
.video-tutorial-section {
  margin: 2rem 0;

  .tutorial-card {
    border-radius: 16px !important;
    border: 1px solid #e0e7ff;
    overflow: hidden;
    background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
  }

  .tutorial-header {
    background: linear-gradient(135deg, #3A67B1 0%, #56BDA3 100%) !important;
    color: white !important;
    padding: 24px !important;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: -50%;
      right: -10%;
      width: 100px;
      height: 100px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 50%;
    }

    .tutorial-title-section {
      display: flex;
      align-items: center;
      flex: 1;

      .title-content {
        .tutorial-title {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0;
          line-height: 1.2;
          color: white;
        }

        .tutorial-subtitle {
          font-size: 1rem;
          margin: 8px 0 0;
          opacity: 0.9;
          color: white;
          font-weight: 400;
        }
      }
    }

    .expand-btn {
      background: rgba(255, 255, 255, 0.2) !important;
      color: white !important;

      &:hover {
        background: rgba(255, 255, 255, 0.3) !important;
      }
    }
  }

  .tutorial-content {
    padding: 32px !important;

    .video-container {
      .video-wrapper {
        position: relative;
        width: 100%;
        height: 0;
        padding-bottom: 56.25%; /* 16:9 aspect ratio */
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
        background: #000;

        .video-iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }
      }
    }

    .video-info {
      .info-section {
        background: rgba(58, 103, 177, 0.05);
        border-radius: 12px;
        padding: 20px;
        border: 1px solid rgba(58, 103, 177, 0.1);

        .info-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #3A67B1;
          margin-bottom: 16px;
          display: flex;
          align-items: center;

          &::before {
            content: '';
            width: 4px;
            height: 20px;
            background: #3A67B1;
            margin-right: 12px;
            border-radius: 2px;
          }
        }

        .learning-points {
          list-style: none;
          padding: 0;
          margin: 0;

          li {
            padding: 8px 0;
            position: relative;
            padding-left: 24px;
            color: #2c3e50;
            font-weight: 500;

            &::before {
              content: '✓';
              position: absolute;
              left: 0;
              color: #56BDA3;
              font-weight: bold;
              font-size: 1.1rem;
            }
          }
        }

        .action-buttons {
          display: flex;
          flex-direction: column;
          gap: 12px;

          .action-btn {
            justify-content: flex-start !important;
            text-transform: none !important;
            font-weight: 600 !important;
            border-radius: 8px !important;
            padding: 12px 16px !important;
            height: auto !important;
          }
        }
      }

      .video-stats {
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid #e0e0e0;

        .stat-item {
          display: flex;
          align-items: center;
          margin-bottom: 8px;

          .stat-text {
            font-size: 0.9rem;
            color: #666;
            margin-left: 4px;
          }
        }
      }
    }
  }
}

/* Responsive Video Tutorial */
@media (max-width: 768px) {
  .video-tutorial-section {
    margin: 1.5rem 0;

    .tutorial-header {
      padding: 20px !important;

      .tutorial-title-section {
        .title-content {
          .tutorial-title {
            font-size: 1.3rem;
          }

          .tutorial-subtitle {
            font-size: 0.9rem;
          }
        }
      }
    }

    .tutorial-content {
      padding: 24px !important;

      .video-info {
        margin-top: 24px;

        .info-section {
          padding: 16px;

          .info-title {
            font-size: 1rem;
          }

          .action-buttons {
            .action-btn {
              padding: 10px 12px !important;
            }
          }
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .video-tutorial-section {
    .tutorial-header {
      padding: 16px !important;

      .tutorial-title-section {
        .title-content {
          .tutorial-title {
            font-size: 1.2rem;
          }

          .tutorial-subtitle {
            font-size: 0.85rem;
          }
        }
      }
    }

    .tutorial-content {
      padding: 16px !important;

      .video-info {
        .info-section {
          padding: 12px;
          margin-bottom: 16px;
        }
      }
    }
  }
}
</style>